/**
 * Inventory - Player inventory with bag slots and equipment slots.
 */

/** All equipment slot keys */
export const EQUIP_SLOT_KEYS = [
  'head', 'shoulders', 'body', 'hands', 'belt', 'legs', 'feet', 'back',
  'mainhand', 'offhand', 'neck', 'ring1', 'ring2'
];

export default class Inventory {
  static MAX_SLOTS = 16;
  static MAX_CARRY_WEIGHT = 100;

  constructor() {
    /** @type {Array<{itemId: string, quantity: number, def: Object}|null>} */
    this.slots = new Array(Inventory.MAX_SLOTS).fill(null);

    /** Equipment slots — one per body position */
    this.equipped = {};
    for (const key of EQUIP_SLOT_KEYS) {
      this.equipped[key] = null;
    }

    /** Player gold */
    this.gold = 0;

    /** Called when inventory changes (for HUD update) */
    this.onChange = null;
  }

  /**
   * Add an item to inventory. Returns true if added.
   */
  addItem(itemId, quantity = 1, def, instanceData = null) {
    // Try stacking first (items with instanceData never stack — they're unique equipment)
    if (def.stackable && !instanceData) {
      for (const slot of this.slots) {
        if (slot && slot.itemId === itemId && !slot.instanceData) {
          slot.quantity += quantity;
          this._notify();
          return true;
        }
      }
    }

    // Find empty slot
    const emptyIdx = this.slots.findIndex(s => s === null);
    if (emptyIdx === -1) return false; // inventory full

    this.slots[emptyIdx] = { itemId, quantity, def, instanceData };
    this._notify();
    return true;
  }

  /**
   * Use a consumable item from a slot.
   * @param {number} slotIndex
   * @param {Object} player - Character with combatStats
   * @returns {boolean} true if used successfully
   */
  useItem(slotIndex, player) {
    const slot = this.slots[slotIndex];
    if (!slot) return false;

    const def = slot.def;
    if (def.type !== 'consumable') {
      // Try equipping non-consumables
      if (def.slot) return this.equipItem(slotIndex);
      return false;
    }

    // Apply effects
    const cs = player.combatStats;
    if (def.effect && cs) {
      if (def.effect.heal) {
        cs.health = Math.min(cs.maxHealth, cs.health + def.effect.heal);
        // Update floating health bar
        if (player._floatingHpFill) {
          const pct = (cs.health / cs.maxHealth) * 100;
          player._floatingHpFill.style.width = `${pct}%`;
          if (player._floatingHpBar) {
            player._floatingHpBar.style.display = pct >= 100 ? 'none' : 'block';
          }
        }
      }
      if (def.effect.restoreMana) {
        cs.mana = Math.min(cs.maxMana, cs.mana + def.effect.restoreMana);
      }
      if (def.effect.restoreStamina) {
        cs.stamina = Math.min(cs.maxStamina, cs.stamina + def.effect.restoreStamina);
      }
    }

    // Consume
    slot.quantity--;
    if (slot.quantity <= 0) {
      this.slots[slotIndex] = null;
    }

    window.game?.audioSystem?.audio?.playUiConfirm();
    this._notify();
    return true;
  }

  /**
   * Resolve the actual equipped slot key for an item.
   * Most items map 1:1 (e.g. 'head' → 'head').
   * Items with slot='ring' go to ring1, then ring2, then swap ring1.
   * @returns {string} the equipped key to use
   */
  _resolveEquipKey(itemSlot) {
    if (itemSlot === 'ring') {
      if (!this.equipped.ring1) return 'ring1';
      if (!this.equipped.ring2) return 'ring2';
      return 'ring1'; // both full — swap ring1
    }
    return itemSlot;
  }

  /**
   * Equip an item from inventory slot.
   */
  equipItem(slotIndex) {
    const slot = this.slots[slotIndex];
    if (!slot || !slot.def.slot) return false;

    // Level requirement check
    if (!this._checkLevelRequirement(slot)) return false;

    const equipKey = this._resolveEquipKey(slot.def.slot);

    // Swap current equipment to inventory
    const current = this.equipped[equipKey];
    this.equipped[equipKey] = slot;

    if (current) {
      this.slots[slotIndex] = current;
    } else {
      this.slots[slotIndex] = null;
    }

    window.game?.audioSystem?.audio?.playUiConfirm();
    this._notify();
    return true;
  }

  /**
   * Equip an item from a bag slot into a specific equipment slot key.
   * Used by drag-and-drop where the target slot is explicit.
   * @param {number} slotIndex - bag slot index
   * @param {string} equipKey - target equipment slot key (e.g. 'ring2')
   * @returns {boolean}
   */
  equipItemToSlot(slotIndex, equipKey) {
    const slot = this.slots[slotIndex];
    if (!slot || !slot.def.slot) return false;

    // Level requirement check
    if (!this._checkLevelRequirement(slot)) return false;

    // Validate the item can go in this slot
    const itemSlot = slot.def.slot;
    if (itemSlot === 'ring') {
      if (equipKey !== 'ring1' && equipKey !== 'ring2') return false;
    } else if (itemSlot !== equipKey) {
      return false;
    }

    const current = this.equipped[equipKey];
    this.equipped[equipKey] = slot;

    if (current) {
      this.slots[slotIndex] = current;
    } else {
      this.slots[slotIndex] = null;
    }

    window.game?.audioSystem?.audio?.playUiConfirm();
    this._notify();
    return true;
  }

  /**
   * Check if inventory contains a specific item.
   */
  hasItem(itemId) {
    return this.slots.some(s => s && s.itemId === itemId) ||
           Object.values(this.equipped).some(s => s && s.itemId === itemId);
  }

  /**
   * Get total equipment bonuses.
   */
  getEquipmentBonuses() {
    let bonusAtk = 0, bonusDef = 0;
    let bonusStr = 0, bonusDex = 0, bonusInt = 0, bonusCon = 0, bonusWis = 0, bonusEnd = 0;
    for (const slot of Object.values(this.equipped)) {
      if (!slot) continue;
      // Prefer level-scaled stats from instanceData, fall back to base def stats
      const stats = slot.instanceData?.scaledStats || slot.def?.stats;
      if (stats) {
        bonusAtk += stats.attackPower || 0;
        bonusDef += stats.defense || 0;
        bonusStr += stats.str || 0;
        bonusDex += stats.dex || 0;
        bonusInt += stats.int || 0;
        bonusCon += stats.con || 0;
        bonusWis += stats.wis || 0;
        bonusEnd += stats.end || 0;
      }
    }
    return { bonusAtk, bonusDef, bonusStr, bonusDex, bonusInt, bonusCon, bonusWis, bonusEnd };
  }

  /**
   * Unequip an item from an equipment slot back to inventory.
   * @param {string} equipKey - slot key (e.g. 'head', 'ring1', 'mainhand')
   * @returns {boolean} true if unequipped
   */
  unequipItem(equipKey) {
    const item = this.equipped[equipKey];
    if (!item) return false;
    const emptyIdx = this.slots.findIndex(s => s === null);
    if (emptyIdx === -1) return false; // bag full
    this.slots[emptyIdx] = item;
    this.equipped[equipKey] = null;
    window.game?.audioSystem?.audio?.playUiConfirm();
    this._notify();
    return true;
  }

  /**
   * Swap two bag slots (either or both may be empty).
   * @param {number} fromIndex
   * @param {number} toIndex
   */
  swapSlots(fromIndex, toIndex) {
    [this.slots[fromIndex], this.slots[toIndex]] = [this.slots[toIndex], this.slots[fromIndex]];
    this._notify();
  }

  /**
   * Remove an item from a bag slot entirely.
   * @param {number} slotIndex
   * @returns {{itemId, quantity, def}|null} the removed item data
   */
  removeItem(slotIndex) {
    const item = this.slots[slotIndex];
    if (!item) return null;
    this.slots[slotIndex] = null;
    this._notify();
    return item;
  }

  /**
   * Add gold.
   */
  addGold(amount) {
    this.gold += amount;
    this._notify();
  }

  /**
   * Remove gold. Returns false if insufficient.
   */
  removeGold(amount) {
    if (this.gold < amount) return false;
    this.gold -= amount;
    this._notify();
    return true;
  }

  /** Clear all items */
  clear() {
    this.slots.fill(null);
    for (const key of EQUIP_SLOT_KEYS) {
      this.equipped[key] = null;
    }
    this._notify();
  }

  /**
   * Get total weight of all carried items (bag + equipped).
   */
  getTotalWeight() {
    let total = 0;
    for (const slot of this.slots) {
      if (slot?.def?.weight) total += slot.def.weight * slot.quantity;
    }
    for (const slot of Object.values(this.equipped)) {
      if (slot?.def?.weight) total += slot.def.weight;
    }
    return Math.round(total * 10) / 10;
  }

  /**
   * Get movement speed multiplier based on weight.
   * No penalty at or below MAX_CARRY_WEIGHT.
   * Progressive slowdown when overweight, floor at 0.5x.
   */
  getSpeedMultiplier() {
    const weight = this.getTotalWeight();
    if (weight <= Inventory.MAX_CARRY_WEIGHT) return 1.0;
    return Math.max(0.5, 1 - 0.5 * (weight - Inventory.MAX_CARRY_WEIGHT) / Inventory.MAX_CARRY_WEIGHT);
  }

  /**
   * Check if the player meets the level requirement for an item.
   * Shows a notification and returns false if requirement not met.
   * @param {Object} slot - inventory slot { itemId, quantity, def, instanceData }
   * @returns {boolean}
   */
  _checkLevelRequirement(slot) {
    const reqLevel = slot.instanceData?.requiredLevel || 0;
    if (reqLevel <= 1) return true;
    const playerLevel = window.game?.progression?.level || 1;
    if (playerLevel >= reqLevel) return true;
    // Show notification
    if (window.game?.hudManager) {
      window.game.hudManager.showNotification(`Requires level ${reqLevel}`, '#ff4444');
    }
    return false;
  }

  /**
   * Remove one quantity of an item by itemId from bag slots.
   * @param {string} itemId
   * @returns {boolean} true if removed
   */
  removeItemById(itemId) {
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      if (slot && slot.itemId === itemId) {
        slot.quantity--;
        if (slot.quantity <= 0) {
          this.slots[i] = null;
        }
        this._notify();
        return true;
      }
    }
    return false;
  }

  _notify() {
    if (this.onChange) this.onChange(this);
  }
}
